import { type ReactNode } from 'react';
import type { Bubble, Vec2 } from '@/utils/bubble-physics';
import { bubblePhotoUrl, type BubbleItem } from '@/hooks/use-home-bubbles';
import { BUBBLE_BASE_SIZE, SIZE_NEAR_FACTOR } from '@/utils/bubble-constants';
import { deriveFromDepth, type BubbleSeed } from './bubble-depth';

export const MAX_BUBBLE_SIZE = Math.round(BUBBLE_BASE_SIZE * SIZE_NEAR_FACTOR);
const EMOJI_RATIO = 0.3;
const SEED_VEL = 0.4;
const FADE_STAGGER_MS = 8;
const FADE_DURATION_MS = 500;

function pickPhoto(photos: BubbleItem[]): string | undefined {
  if (photos.length === 0) return undefined;
  const p = photos[Math.floor(Math.random() * photos.length)];
  return p ? bubblePhotoUrl(p) : undefined;
}

function makeBubble(photos: BubbleItem[], pos: Vec2, seed: BubbleSeed): Bubble {
  const wantPhoto = photos.length > 0 && Math.random() >= EMOJI_RATIO;
  const photoUrl = wantPhoto ? pickPhoto(photos) : undefined;
  const d = deriveFromDepth(seed.depth);
  return {
    type: photoUrl ? 'photo' : 'emoji',
    pos: { x: pos.x, y: pos.y },
    vel: {
      x: (Math.random() - 0.5) * SEED_VEL,
      y: (Math.random() - 0.5) * SEED_VEL,
    },
    size: d.size,
    depth: seed.depth,
    layer: seed.layer,
    maxSpeed: d.maxSpeed,
    ...(photoUrl ? { photoUrl } : {}),
  };
}

export function createBubbles(
  photos: BubbleItem[],
  positions: Vec2[],
  seeds: BubbleSeed[]
): Bubble[] {
  return positions.map((p, i) => {
    const seed = seeds[i] ?? { depth: 0.5, layer: 0 };
    return makeBubble(photos, p, seed);
  });
}

function slotStyle(b: Bubble, idx: number): React.CSSProperties {
  const d = deriveFromDepth(b.depth);
  return {
    width: b.size,
    height: b.size,
    // Initial transform = spawn position so the first paint already
    // sits where physics will hold it. Without this the bubble flashes
    // at (0, 0) for one frame before the RAF tick takes over.
    transform: `translate3d(${b.pos.x}px, ${b.pos.y}px, 0)`,
    filter: d.blur > 0 ? `blur(${d.blur.toFixed(1)}px)` : undefined,
    // Cap below HomeContent (z-10). Bubbles still paint by depth
    // within the field (near in front of far) but never above the UI.
    zIndex: Math.floor(b.depth * 9),
    willChange: 'transform',
    animation: `opacity-in ${FADE_DURATION_MS}ms ease-out ${idx * FADE_STAGGER_MS}ms both`,
    ['--bubble-opacity' as string]: d.opacity.toFixed(2),
  } as React.CSSProperties;
}

function PhotoBubble({ url }: { url: string }): ReactNode {
  return (
    <img
      src={url}
      alt=""
      className="h-full w-full rounded-full object-cover object-[center_35%] ring-1 ring-white/15"
    />
  );
}

function EmojiBubble({ size }: { size: number }): ReactNode {
  return (
    <span
      className="flex h-full w-full select-none items-center justify-center"
      style={{ fontSize: Math.round(size * 0.7) }}
    >
      🤑
    </span>
  );
}

type SlotProps = {
  bubble: Bubble;
  idx: number;
  refCb: (el: HTMLDivElement | null) => void;
};

export function Slot({ bubble, idx, refCb }: SlotProps): ReactNode {
  return (
    <div ref={refCb} className="absolute" style={slotStyle(bubble, idx)}>
      {bubble.type === 'photo' && bubble.photoUrl ? (
        <PhotoBubble url={bubble.photoUrl} />
      ) : (
        <EmojiBubble size={bubble.size} />
      )}
    </div>
  );
}
